import { useRef, useState } from 'react'
import { Lock, LockOpen } from 'lucide-react'

import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { CalloutNote } from '@/components/CalloutNote'
import { CalloutPhotoNote } from '@/components/CalloutPhotoNote'
import {
  PhotoNoteComposer,
  type PhotoNoteComposerHandle,
  type PhotoNoteReplace,
  type PhotoNoteSubmit,
} from '@/components/PhotoNoteComposer'
import { InlineNoteMarkdown, NoteMarkdown } from '@/components/NoteMarkdown'
import { statusIsBlocking } from '@/db/domain/statusRules'
import { formatServiceStatus } from '@/help/serviceStatus'
import { toggleTaskInMarkdown } from '@/lib/noteMarkdown'
import {
  differingOccurredLabel,
  formatRelativeDateTime,
  formatShortRelativeDateTime,
} from '@/lib/relativeDateTime'
import type { ServiceStatus } from '@/lib/schemas/db'

function statusChangeDotClass(newStatus: ServiceStatus): string {
  if (statusIsBlocking(newStatus)) return 'bg-rose-500'
  if (newStatus === 'Invoiced') return 'bg-emerald-500'
  return 'bg-blue-500'
}

/**
 * Audit trail carried by every note. Notes stay editable even once the service
 * is locked, so what makes that safe is that nothing is lost: earlier versions
 * survive as revisions and a delete keeps its content as a tombstone.
 */
export interface NoteAuditFields {
  /** Superseded versions, oldest first. */
  revisions?: Array<{
    text?: string
    caption?: string
    /** The cover photo this version showed, still stored and retrievable. */
    photoUrl?: string
    /** Every photo the version's group showed, when it was a group. */
    photoUrls?: string[]
    at: string
    authorName: string
  }>
  /** Set when soft-deleted; the note renders as a tombstone. */
  deletedAt?: string
  deletedByName?: string
  /**
   * True when this note was *changed* during a period the service was closed.
   *
   * Where the note sits relative to the lock markers already says when it was
   * written, so that needs no badge. What position cannot show is a note
   * created while the record was open and rewritten after it closed — which is
   * the edit most worth noticing, since it alters what the record claimed.
   */
  editedWhileLocked?: boolean
  /** True when the note was deleted during a period the service was closed. */
  deletedWhileLocked?: boolean
}

export interface CalloutNotesSectionTextNote extends NoteAuditFields {
  id: string
  text: string
  authorName: string
  createdAt: string
  kind?: 'text'
}

export interface CalloutNotesSectionPhotoNote extends NoteAuditFields {
  id: string
  authorName: string
  createdAt: string
  kind: 'photo'
  photoUrl: string
  /** Every photo the note currently shows; single notes may omit this. */
  photoUrls?: string[]
  caption?: string
}

export interface CalloutNotesSectionStatusChange {
  id: string
  kind: 'status_change'
  authorName: string
  createdAt: string
  /** When the event really happened, if that differs from when it was logged. */
  occurredAt?: string
  previousStatus: ServiceStatus
  newStatus: ServiceStatus
  reason?: string
}

/**
 * A status change that opened or closed a locked period.
 *
 * These are ordinary status changes promoted to a heavier treatment: closing a
 * record is the event the rest of the feed is read against, so it spans the
 * width as a divider rather than sitting in the column like a routine move.
 * Rendering it as a feed entry also means it lands in chronological order for
 * free, which a separately-positioned divider could only approximate.
 */
export interface CalloutNotesSectionLockChange {
  id: string
  kind: 'lock'
  authorName: string
  createdAt: string
  occurredAt?: string
  previousStatus: ServiceStatus
  newStatus: ServiceStatus
  reason?: string
  /** True when this reopened the record rather than closing it. */
  isReopen: boolean
}

export type CalloutNotesSectionNote =
  | CalloutNotesSectionTextNote
  | CalloutNotesSectionPhotoNote
  | CalloutNotesSectionStatusChange
  | CalloutNotesSectionLockChange

export interface CalloutNotesSectionProps {
  notes: CalloutNotesSectionNote[]
  /** Heading above the feed. The customer thread isn't work notes. */
  feedLabel?: string
  newNoteAuthorName: string
  /** When false, hides the "New note" composer area. */
  showNewNoteArea?: boolean
  /** Optional id of the note currently being edited. */
  editingNoteId?: string | null
  /** Start editing a specific note. */
  onStartEditNote?: (id: string) => void
  /** Cancel editing the current note. */
  onCancelEditNote?: () => void
  /** Save changes to a note's text. */
  onSaveEditNote?: (id: string, text: string) => void
  /** Add a new note using the "New note" area. */
  onAddNote?: (text: string) => boolean
  /** Create a new photo note (cropped + compressed by the composer). */
  onSubmitPhotoNote?: (input: PhotoNoteSubmit) => void
  /** Replace the image for a specific photo note. */
  onReplacePhotoNote?: (id: string, input: PhotoNoteReplace) => void
  /** Error to surface in the photo composer (e.g. a failed save). */
  photoError?: string | null
  /** Clear the photo composer error. */
  onClearPhotoError?: () => void
  /** Delete a specific note (text or photo). */
  onDeleteNote?: (id: string) => void
  /**
   * Why this viewer may not edit notes here. When set, tapping a read-mode
   * checkbox shows this instead of opening the editor — the checkbox stays
   * visibly tappable so the app explains the refusal rather than ignoring
   * the tap.
   */
  editDeniedReason?: string
}

type AuditableNote = CalloutNotesSectionTextNote | CalloutNotesSectionPhotoNote

/** A feed entry someone wrote, as opposed to one the status machine emitted. */
export function isAuthoredNote(
  note: CalloutNotesSectionNote,
): note is CalloutNotesSectionTextNote | CalloutNotesSectionPhotoNote {
  return note.kind !== 'status_change' && note.kind !== 'lock'
}

function ChangedWhileLockedBadge(props: { label: string }) {
  return (
    <span
      className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-800"
      title="This note was changed while the record was closed"
    >
      {props.label}
    </span>
  )
}

/**
 * The trail under a note: whether it came after the record was locked, and
 * what it said before it was edited.
 */
function NoteAuditFooter(props: { note: AuditableNote }) {
  const { note } = props
  const [historyOpen, setHistoryOpen] = useState(false)
  const revisions = note.revisions ?? []

  if (revisions.length === 0) return null

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 pl-2">
      {note.editedWhileLocked ? (
        <ChangedWhileLockedBadge label="edited after locking" />
      ) : null}
      {revisions.length > 0 ? (
        <button
          type="button"
          className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-px text-[10px] font-medium text-slate-600 hover:bg-slate-100"
          onClick={() => setHistoryOpen((open) => !open)}
        >
          edited ×{revisions.length} {historyOpen ? '▴' : '▾'}
        </button>
      ) : null}
      {historyOpen && revisions.length > 0 ? (
        <ul className="w-full space-y-1 border-l-2 border-slate-200 pl-2">
          {revisions.map((revision) => (
            <li key={revision.at} className="text-[11px] text-slate-500">
              {revision.photoUrl ? (
                <span className="mb-1 flex flex-wrap gap-1">
                  {(revision.photoUrls?.length
                    ? revision.photoUrls
                    : [revision.photoUrl]
                  ).map((url) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="block w-fit"
                      title="Superseded photo — opens full size"
                    >
                      <img
                        src={url}
                        alt={`Superseded photo, replaced ${formatShortRelativeDateTime(revision.at)}`}
                        className="h-14 w-14 rounded border border-slate-200 object-cover opacity-70"
                      />
                    </a>
                  ))}
                </span>
              ) : null}
              {revision.text || revision.caption ? (
                <span className="line-through">
                  {revision.text || revision.caption}
                </span>
              ) : revision.photoUrl ? null : (
                <span className="line-through">(empty)</span>
              )}
              <span>
                {revision.text || revision.caption ? ' ' : ''}
                — replaced by {revision.authorName}
                {' · '}
                {formatShortRelativeDateTime(revision.at)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * A soft-deleted note. The content is deliberately still here — deleting a
 * note must never quietly erase what a locked record was based on.
 */
function NoteTombstone(props: { note: AuditableNote }) {
  const { note } = props
  const [open, setOpen] = useState(false)
  const body = note.kind === 'photo' ? note.caption : note.text

  return (
    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-2.5 py-2">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="text-[11px] italic text-slate-400">
          {note.kind === 'photo' ? 'Photo note' : 'Note'} deleted
          {note.deletedByName ? ` by ${note.deletedByName}` : ''}
          {note.deletedAt
            ? ` · ${formatShortRelativeDateTime(note.deletedAt)}`
            : ''}
        </span>
        <span className="flex items-center gap-1.5">
          {note.deletedWhileLocked ? (
            <ChangedWhileLockedBadge label="deleted after locking" />
          ) : null}
          <span className="text-[11px] text-slate-400">{open ? '▴' : '▾'}</span>
        </span>
      </button>
      {open ? (
        <div className="mt-1.5 border-t border-slate-200 pt-1.5">
          {note.kind === 'photo' ? (
            <img
              src={note.photoUrl}
              // `||` not `??` — a captionless photo note has an empty-string
              // caption, which would otherwise become an empty alt.
              alt={body || 'Deleted photo note'}
              className="mb-1 max-h-40 rounded-md opacity-60"
            />
          ) : null}
          {/* A photo note's content is its image, so a missing caption leaves
              nothing to strike through — "(empty note)" would misdescribe what
              was deleted. Only a text note with no text is genuinely empty. */}
          {body || note.kind !== 'photo' ? (
            <p className="text-xs whitespace-pre-wrap text-slate-500 line-through">
              {body || '(empty note)'}
            </p>
          ) : null}
          <p className="mt-0.5 text-[11px] text-slate-400">
            {note.authorName} · {formatShortRelativeDateTime(note.createdAt)}
          </p>
        </div>
      ) : null}
    </div>
  )
}

export function CalloutNotesSection(props: CalloutNotesSectionProps) {
  const {
    notes,
    feedLabel = 'Work Notes',
    newNoteAuthorName,
    showNewNoteArea = true,
    editingNoteId,
    onStartEditNote,
    onCancelEditNote,
    onSaveEditNote,
    onAddNote,
    onSubmitPhotoNote,
    onReplacePhotoNote,
    photoError,
    onClearPhotoError,
    onDeleteNote,
    editDeniedReason,
  } = props

  const composerRef = useRef<PhotoNoteComposerHandle>(null)
  const canAddPhoto = Boolean(onSubmitPhotoNote)
  const hasNotes = notes.length > 0
  // Same behaviour as the side panel: while the photo card is open it takes
  // over — the new-note area would just be a second, dead composer.
  const [photoOpen, setPhotoOpen] = useState(false)

  // A checkbox tapped in read mode opens the editor with that one box
  // already flipped: the draft is pre-dirtied so Save is live, and nothing
  // is written until the tech confirms it. Keyed by note id so switching
  // notes never carries a stale draft across.
  const [pendingToggle, setPendingToggle] = useState<{
    noteId: string
    text: string
  } | null>(null)
  const [deniedNoteId, setDeniedNoteId] = useState<string | null>(null)

  const canEditNotes = Boolean(onStartEditNote && onSaveEditNote)
  // Tappable when a tap leads somewhere: into the editor, or into an
  // explanation of why not. A view that is simply read-only (the invoice
  // summary, a print view) renders inert boxes instead of live-looking ones
  // that do nothing.
  const checkboxesAreLive = canEditNotes || Boolean(editDeniedReason)

  const handleToggleTask = (
    noteId: string,
    sourceText: string,
    taskIndex: number,
  ) => {
    if (!canEditNotes) {
      setDeniedNoteId(noteId)
      return
    }
    const next = toggleTaskInMarkdown(sourceText, taskIndex)
    if (next === null) return
    setPendingToggle({ noteId, text: next })
    onStartEditNote?.(noteId)
  }

  // The pending flip belongs to the note being edited; anything else is a
  // leftover from a note the user moved away from.
  const pendingToggleFor = (noteId: string) =>
    editingNoteId === noteId && pendingToggle?.noteId === noteId
      ? pendingToggle.text
      : undefined

  const clearPendingToggle = () => setPendingToggle(null)

  return (
    <div className="space-y-3">
      <div className="space-y-1 text-xs">
        <FormCapsLabel>{feedLabel}</FormCapsLabel>
      </div>

      <div className="space-y-2">
        {hasNotes ? (
          notes.map((note) => {
            // Closing or reopening a record spans the feed instead of sitting
            // in the timestamp column: it is the boundary everything around it
            // is read against, and it carries the same facts a routine status
            // row would, so it replaces that row rather than joining it.
            if (note.kind === 'lock') {
              return (
                <div
                  key={note.id}
                  className="flex items-center gap-2 py-0.5 text-[11px] font-medium text-slate-500"
                >
                  <span className="h-px flex-1 bg-slate-200" />
                  {note.isReopen ? (
                    <LockOpen className="h-3 w-3" />
                  ) : (
                    <Lock className="h-3 w-3" />
                  )}
                  <span className="text-center">
                    {note.isReopen
                      ? `Reopened as ${formatServiceStatus(note.newStatus)}`
                      : `${formatServiceStatus(note.newStatus)} — locked`}
                    {note.authorName ? ` by ${note.authorName}` : ''}
                    {' · '}
                    {formatShortRelativeDateTime(note.createdAt)}
                    {note.reason ? (
                      <span className="font-normal text-slate-400">
                        {' · '}
                        <InlineNoteMarkdown text={note.reason} />
                      </span>
                    ) : null}
                  </span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
              )
            }

            if (note.kind === 'status_change') {
              return (
                <div
                  key={note.id}
                  className="grid grid-cols-[56px_1fr] gap-2.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2"
                >
                  <div
                    className="font-mono text-[11px] leading-normal text-slate-400"
                    title={formatRelativeDateTime(note.createdAt)}
                  >
                    {formatShortRelativeDateTime(note.createdAt)}
                  </div>
                  <div>
                    <div className="flex items-baseline gap-1.5">
                      <span
                        className={`h-1.5 w-1.5 shrink-0 -translate-y-px rounded-full ${statusChangeDotClass(note.newStatus)}`}
                      />
                      <span className="text-xs leading-snug text-slate-900">
                        Status → {formatServiceStatus(note.newStatus)}
                      </span>
                    </div>
                    {/* The row is stamped with when it was logged; say when it
                        actually occurred only when that reads differently. */}
                    {differingOccurredLabel(note.occurredAt, note.createdAt) ? (
                      <div className="mt-0.5 pl-3 text-[11px] text-slate-500">
                        Occurred{' '}
                        {differingOccurredLabel(note.occurredAt, note.createdAt)}
                      </div>
                    ) : null}
                    {/* Reasons carry the same markdown notes do — a blocked
                        reason is often a short checklist of what is still
                        outstanding. Checkboxes here are display-only: a
                        reason is amended through the status flow, never by
                        tapping the feed. */}
                    {note.reason ? (
                      statusIsBlocking(note.newStatus) ? (
                        <div className="mt-0.5 flex items-start gap-1 pl-3 text-[11.5px] leading-snug whitespace-normal text-rose-700">
                          <span className="shrink-0">⚠</span>
                          <NoteMarkdown text={note.reason} />
                        </div>
                      ) : (
                        <NoteMarkdown
                          text={note.reason}
                          className="mt-0.5 pl-3 text-[11.5px] leading-snug whitespace-normal text-slate-500"
                        />
                      )
                    ) : null}
                    <div className="mt-0.5 pl-3 text-[11px] text-slate-400">
                      {note.authorName}
                    </div>
                  </div>
                </div>
              )
            }

            if (note.deletedAt) {
              return (
                <NoteTombstone
                  key={note.id}
                  note={note}
                />
              )
            }

            if (note.kind === 'photo') {
              const isEditing = editingNoteId === note.id

              return (
                <div key={note.id}>
                <CalloutPhotoNote
                  authorName={note.authorName}
                  createdAt={note.createdAt}
                  photoUrl={note.photoUrl}
                  photoUrls={note.photoUrls}
                  caption={note.caption}
                  mode={isEditing ? 'edit' : 'read'}
                  enablePreview
                  initialDraftCaption={pendingToggleFor(note.id)}
                  onToggleTask={
                    checkboxesAreLive
                      ? (taskIndex) =>
                          handleToggleTask(
                            note.id,
                            note.caption ?? '',
                            taskIndex,
                          )
                      : undefined
                  }
                  notice={
                    deniedNoteId === note.id ? editDeniedReason : undefined
                  }
                  showEditButton={Boolean(onStartEditNote)}
                  showDeleteButton={Boolean(onDeleteNote)}
                  onClickEdit={
                    onStartEditNote
                      ? () => onStartEditNote(note.id)
                      : undefined
                  }
                  onClickCancel={() => {
                    clearPendingToggle()
                    onCancelEditNote?.()
                  }}
                  onClickReplacePhoto={
                    onReplacePhotoNote
                      ? () => composerRef.current?.startReplace(note.id)
                      : undefined
                  }
                  onClickAdjustPhoto={
                    onReplacePhotoNote
                      ? () =>
                          composerRef.current?.startAdjust(
                            note.id,
                            note.photoUrl,
                          )
                      : undefined
                  }
                  onClickSave={
                    onSaveEditNote
                      ? (nextCaption) => {
                          clearPendingToggle()
                          onSaveEditNote(note.id, nextCaption)
                        }
                      : undefined
                  }
                  onClickDelete={
                    onDeleteNote
                      ? () => onDeleteNote(note.id)
                      : undefined
                  }
                />
                <NoteAuditFooter
                  note={note}
                />
              </div>
              )
            }

            const isEditing = editingNoteId === note.id

          return (
            <div key={note.id}>
            <CalloutNote
              authorName={note.authorName}
              createdAt={note.createdAt}
              text={note.text}
              mode={isEditing ? 'edit' : 'read'}
              initialDraftText={pendingToggleFor(note.id)}
              onToggleTask={
                checkboxesAreLive
                  ? (taskIndex) =>
                      handleToggleTask(note.id, note.text, taskIndex)
                  : undefined
              }
              notice={deniedNoteId === note.id ? editDeniedReason : undefined}
              showEditButton={Boolean(onStartEditNote)}
              showDeleteButton={Boolean(onDeleteNote)}
                onClickEdit={
                  onStartEditNote
                    ? () => onStartEditNote(note.id)
                    : undefined
                }
                onClickCancel={() => {
                  clearPendingToggle()
                  onCancelEditNote?.()
                }}
                onClickSave={
                  onSaveEditNote
                    ? (nextText) => {
                        clearPendingToggle()
                        onSaveEditNote(note.id, nextText)
                      }
                    : undefined
                }
                onClickDelete={
                  onDeleteNote
                    ? () => onDeleteNote(note.id)
                    : undefined
                }
              />
              <NoteAuditFooter
                note={note}
              />
            </div>
            )
          })
        ) : (
          <p className="text-xs text-slate-500">
            No notes yet. Use notes to capture extra context from site.
          </p>
        )}
      </div>

      {showNewNoteArea && !photoOpen ? (
        <div className="mt-4 space-y-2 text-xs">
          <FormCapsLabel>New Note</FormCapsLabel>
          <CalloutNote
            authorName={newNoteAuthorName}
            mode="new"
            onClickAddNote={onAddNote}
            onClickAddPhoto={
              canAddPhoto ? () => composerRef.current?.startAdd() : undefined
            }
          />
        </div>
      ) : null}

      {/* Mounted whenever photos are supported so startAdd/startReplace can be
          driven imperatively from the new-note area and photo notes above. */}
      {canAddPhoto && onSubmitPhotoNote ? (
        <PhotoNoteComposer
          ref={composerRef}
          onSubmitPhoto={onSubmitPhotoNote}
          onReplacePhoto={onReplacePhotoNote}
          error={photoError}
          onClearError={onClearPhotoError}
          onOpenChange={setPhotoOpen}
        />
      ) : null}
    </div>
  )
}
